import { t } from "i18next";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldTick } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import {
    surveyPublicSubmitUrl,
    surveyPublicUrl,
} from "../../helperData/ApiAddress";
import {
    SurveyFormRenderer,
    MultiStepSurveyFormRenderer,
} from "../../modules/form-builder";
import { SURVEY_STATUS, SURVEY_FORM_DISPLAY_STEP } from "../surveys/constants";
import SurveyIdentityPanel from "../surveys/SurveyIdentityPanel";
import {
    RewardBadge,
    RewardStatus,
} from "../../features/incentives/Rewards";
import "./style.scss";

const PAGE_STATE = {
    LOADING: "loading",
    READY: "ready",
    CLOSED: "closed",
    USED: "used",
    INVALID: "invalid",
    LOGIN: "login",
    IDENTITY: "identity",
    DONE: "done",
};

export default function SurveyResponse() {
    const { token } = useParams();
    const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
    const [definition, setDefinition] = useState(null);
    const [replies, setReplies] = useState({});
    const [consentChecked, setConsentChecked] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [featureValues, setFeatureValues] = useState({});
    const [featureErrors, setFeatureErrors] = useState({});
    const sentinelRef = useRef(null);
    const [headerCollapsed, setHeaderCollapsed] = useState(false);
    const [rewardStatus, setRewardStatus] = useState(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => setHeaderCollapsed(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [pageState]);

    const authHeaders = () => {
        const auth = sessionStorage.getItem("auth");
        return auth ? { Authorization: `Bearer ${auth}` } : {};
    };

    useEffect(() => {
        axios
            .get(surveyPublicUrl(token), { headers: authHeaders() })
            .then((r) => {
                const data = r.data;
                setDefinition(data);
                if (data.already_completed) {
                    setPageState(PAGE_STATE.USED);
                } else if (data.status !== SURVEY_STATUS.OPEN) {
                    setPageState(PAGE_STATE.CLOSED);
                } else {
                    setPageState(PAGE_STATE.READY);
                }
            })
            .catch((e) => {
                const status = e.response?.status;
                if (status === 410) {
                    setPageState(PAGE_STATE.USED);
                } else if (status === 403) {
                    setPageState(PAGE_STATE.CLOSED);
                } else if (status === 401) {
                    setPageState(PAGE_STATE.LOGIN);
                } else {
                    setPageState(PAGE_STATE.INVALID);
                }
            });
    }, [token]);

    const needsIdentity = Boolean(
        definition && !definition.anonymous_responses
    );

    const handleReplyChange = (criteriaInfoId, patch) => {
        setReplies((prev) => ({ ...prev, [criteriaInfoId]: patch }));
    };

    const validate = () => {
        for (const criterion of definition.criteria || []) {
            const reply = replies[criterion.id];
            if (!reply || (reply.state == null && !reply.description && !reply.file)) {
                return false;
            }
        }
        return true;
    };

    const validateFeatures = () => {
        const nextErrors = {};
        for (const feature of definition.features || []) {
            if (feature.mandatory && !(featureValues[feature.id] || "").trim()) {
                nextErrors[feature.id] = t("survey.response.identity.field.required");
            }
        }
        setFeatureErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleFinishQuestions = () => {
        setSubmitError("");
        if (!validate()) {
            setSubmitError(t("survey.response.required.error"));
            return;
        }
        if (needsIdentity) {
            setPageState(PAGE_STATE.IDENTITY);
            return;
        }
        submitResponse();
    };

    const submitResponse = () => {
        setSubmitError("");
        if (needsIdentity) {
            if (!sessionStorage.getItem("auth")) {
                setSubmitError(t("survey.response.identity.login.first"));
                return;
            }
            if (!validateFeatures()) {
                setSubmitError(t("survey.response.identity.fields.error"));
                return;
            }
        }
        setSubmitLoading(true);
        const payload = {
            captcha_token: "",
            replies: (definition.criteria || []).map((criterion) => {
                const reply = replies[criterion.id] || {};
                return {
                    criteria_info: criterion.id,
                    criteria_state: reply.state?.id ?? null,
                    description: reply.description || "",
                    file: reply.file || "",
                };
            }),
            feature_replies: (definition.features || [])
                .map((feature) => ({
                    feature: feature.id,
                    value: (featureValues[feature.id] || "").trim(),
                }))
                .filter((row) => row.value),
        };
        axios
            .post(surveyPublicUrl(token), {}, { headers: authHeaders() })
            .then((startResponse) =>
                axios.post(
                    surveyPublicSubmitUrl(token),
                    {
                        ...payload,
                        response_token: startResponse.data.response_token,
                    },
                    { headers: authHeaders() }
                )
            )
            .then((response) => {
                setRewardStatus(
                    response.data?.reward_status ||
                        response.data?.reward?.status ||
                        "pending"
                );
                setPageState(PAGE_STATE.DONE);
            })
            .catch((e) => {
                if (e.response?.status === 410) {
                    setPageState(PAGE_STATE.USED);
                } else if (e.response?.status === 403) {
                    setPageState(PAGE_STATE.CLOSED);
                } else if (e.response?.status === 401) {
                    setPageState(PAGE_STATE.IDENTITY);
                    setSubmitError(t("survey.response.identity.login.first"));
                } else {
                    setSubmitError(t("survey.response.submit.error"));
                }
            })
            .finally(() => setSubmitLoading(false));
    };

    const renderNotice = (messageKey, reward) => (
        <div className="survey-response-page">
            <div className="survey-response-card survey-response-notice">
                <ShieldTick size={48} aria-hidden="true" />
                <h1>{t("survey.response.brand")}</h1>
                <p>{t(messageKey)}</p>
                {reward}
            </div>
        </div>
    );

    if (pageState === PAGE_STATE.LOADING) {
        return (
            <div className="survey-response-page">
                <div className="survey-response-card survey-response-notice">
                    <p>{t("loading")}</p>
                </div>
            </div>
        );
    }
    if (pageState === PAGE_STATE.INVALID) {
        return renderNotice("survey.response.invalid");
    }
    if (pageState === PAGE_STATE.CLOSED) {
        return renderNotice("survey.response.closed");
    }
    if (pageState === PAGE_STATE.USED) {
        return renderNotice("survey.response.used");
    }
    if (pageState === PAGE_STATE.LOGIN) {
        return renderNotice("survey.response.login.required");
    }
    if (pageState === PAGE_STATE.DONE) {
        return renderNotice(
            "survey.response.submit.success",
            <RewardStatus status={rewardStatus} />
        );
    }

    const isStepForm =
        definition.form_display_mode === SURVEY_FORM_DISPLAY_STEP;
    const identityStep = pageState === PAGE_STATE.IDENTITY;

    return (
        <div className={`survey-response-page${headerCollapsed ? " header-collapsed" : ""}`}>
            <div ref={sentinelRef} className="survey-response-sentinel" />
            <div className="survey-response-sticky-bar" aria-hidden={!headerCollapsed}>
                <h2>{definition.title}</h2>
                <RewardBadge
                    action="survey_submit"
                    targetType="survey"
                    targetId={definition.id}
                    opportunity={definition.incentive_opportunity}
                />
                {definition.subject_title && (
                    <span>{definition.subject_title}</span>
                )}
            </div>
            <div className="survey-response-card">
                <header className="survey-response-header">
                    <span className="survey-response-brand">
                        {t("survey.response.brand")}
                    </span>
                    <div className="survey-response-title-row">
                        <h1>{definition.title}</h1>
                        <RewardBadge
                            action="survey_submit"
                            targetType="survey"
                            targetId={definition.id}
                            opportunity={definition.incentive_opportunity}
                        />
                    </div>
                    <div className="survey-response-header-details">
                        {definition.subject_title && (
                            <span className="survey-response-subject">
                                {definition.subject_title}
                            </span>
                        )}
                        {definition.description && (
                            <p>{definition.description}</p>
                        )}
                        {definition.anonymous_responses && (
                            <div className="survey-response-anonymous">
                                <ShieldTick size={18} aria-hidden="true" />
                                <span>{t("survey.response.anonymous.notice")}</span>
                            </div>
                        )}
                    </div>
                </header>
                {identityStep ? (
                    <SurveyIdentityPanel
                        features={definition.features || []}
                        featureValues={featureValues}
                        onFeatureChange={(featureId, value) => {
                            setFeatureValues((prev) => ({
                                ...prev,
                                [featureId]: value,
                            }));
                            setFeatureErrors((prev) => ({
                                ...prev,
                                [featureId]: undefined,
                            }));
                        }}
                        featureErrors={featureErrors}
                        onBack={() => setPageState(PAGE_STATE.READY)}
                        onSubmit={submitResponse}
                        submitLoading={submitLoading}
                    />
                ) : isStepForm ? (
                    <MultiStepSurveyFormRenderer
                        criteria={definition.criteria || []}
                        replies={replies}
                        onChange={handleReplyChange}
                        onFinish={handleFinishQuestions}
                        finishCaption={
                            needsIdentity
                                ? t("next.label")
                                : t("finish.label")
                        }
                        finishLoading={submitLoading}
                        finishDisabled={
                            Boolean(definition.consent_text) && !consentChecked
                        }
                    />
                ) : (
                    <SurveyFormRenderer
                        criteria={definition.criteria || []}
                        replies={replies}
                        onChange={handleReplyChange}
                    />
                )}
                {!identityStep && definition.consent_text && (
                    <label className="survey-response-consent">
                        <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(e) =>
                                setConsentChecked(e.target.checked)
                            }
                            aria-label={t("survey.response.consent")}
                        />
                        <span>
                            {t("survey.response.consent")}:{" "}
                            {definition.consent_text}
                        </span>
                    </label>
                )}
                {submitError && (
                    <p className="survey-response-error" role="alert" aria-live="polite">
                        {submitError}
                    </p>
                )}
                {!identityStep && !isStepForm && (
                    <DBButton
                        className="survey-response-submit"
                        caption={
                            needsIdentity
                                ? t("next.label")
                                : t("survey.response.submit")
                        }
                        loading={submitLoading}
                        disabled={
                            Boolean(definition.consent_text) && !consentChecked
                        }
                        onClick={handleFinishQuestions}
                    />
                )}
            </div>
        </div>
    );
}
